import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import {
  BreakConfigurationFragment,
  TimePlanningPlannedBreakBreakTypeEnum as BreakType,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import TextField from 'modules/core/components/TextField'
import i18n from 'modules/core/lib/i18n'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import type EmployeeModel from 'modules/employees/store/models/Employee'
import EmployeesSection from 'modules/shift_management/components/ShiftManagement/EmployeesSection'
import PlannedBreaksSelector from 'modules/shift_management/components/ShiftManagement/PlannedBreaksSelector'
import ShiftLocationPicker from 'modules/shift_management/components/ShiftManagement/ShiftLocationPicker'
import ShiftNotesField from 'modules/shift_management/components/ShiftManagement/ShiftNotesField'
import ShiftOvertimeCheckbox from 'modules/shift_management/components/ShiftManagement/ShiftOvertimeCheckbox'
import ShiftTimePicker from 'modules/shift_management/components/ShiftManagement/ShiftTimePicker'
import TimePeriodSelector from 'modules/shift_management/components/ShiftManagement/TimePeriodSelector'
import TooltipIcon from 'modules/shift_management/components/ShiftManagement/TooltipIcon'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { usePermissions } from 'modules/shift_management/hooks/usePermissions'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectionState } from 'modules/shift_management/hooks/useSelection'
import { ShiftManagementFeatureFlags } from 'modules/shift_management/lib/consts/ShiftManagementFeatureFlags'
import type CommonSelectorsForm from 'modules/shift_management/lib/forms/CommonSelectorsForm'
import type DefaultShiftForm from 'modules/shift_management/lib/forms/DefaultShiftForm'
import type PlannedBreakForm from 'modules/shift_management/lib/forms/PlannedBreakForm'
import type ShiftForm from 'modules/shift_management/lib/forms/ShiftForm'
import { Leave } from 'modules/shift_management/lib/types'

import SaveShiftToggle from '../SaveShiftToggle'

interface Props {
  form: ShiftForm
  defaultShiftForm: DefaultShiftForm
  daysByRow?: Record<string, string[]>
  commonSelectorsForm: CommonSelectorsForm
  selectableEmployees: EmployeeModel[]
  selectedEmployees: number[]
  setSelectedEmployees: React.Dispatch<React.SetStateAction<number[]>>
  onAddBreak: (breakConfiguration: BreakConfigurationFragment, breakType: BreakType) => void
  plannedBreakForms: PlannedBreakForm[]
  onDeleteBreak: (breakForm: PlannedBreakForm) => void
  leave?: Leave
  contractVersions: ContractVersions
}

const CustomShiftSection = ({
  form,
  defaultShiftForm,
  commonSelectorsForm,
  selectableEmployees,
  selectedEmployees,
  setSelectedEmployees,
  onAddBreak,
  plannedBreakForms,
  onDeleteBreak,
  leave,
  contractVersions,
}: Props) => {
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const [{ view }] = useSchedule()
  const { isEnabled: hasSelection } = useSelectionState()
  const [{ canManageDefaultShifts }] = usePermissions()

  const locationsEnabled = hasFeatureFlag(ShiftManagementFeatureFlags.DEV_LOCATION_WORK_AREAS)
  const showLocation = !hasSelection || !view.isByAreas
  const hasBreaks = hasFeatureFlag(ShiftManagementFeatureFlags.TIME_SETTINGS_BREAKS)

  const startTimeField = form.get('start_time')
  const endTimeField = form.get('end_time')

  const showTimeSelector = !hasSelection || (!!leave && view.isWeekly)

  return (
    <Box gap="s16">
      <EmployeesSection
        form={commonSelectorsForm}
        selectableEmployees={selectableEmployees}
        selectedEmployees={selectedEmployees}
        setSelectedEmployees={setSelectedEmployees}
        fromLeave={!!leave}
        contractVersions={contractVersions}
      />
      <Box gap="s16" flexDirection="row" justifyContent="center" alignItems="center">
        <TooltipIcon.Medium
          icon="align-text-left"
          label={i18n.t('shift_management.preview_cell.shift_name')}
          color="grey700"
        />
        <TextField
          placeholder={i18n.t('shift_management.preview_cell.shift_name')}
          value={form.get('name').value.toString()}
          onChange={(name) => form.get('name').set(name)}
        />
      </Box>
      <ShiftTimePicker startField={startTimeField} endField={endTimeField} newVersion />
      <ShiftOvertimeCheckbox form={form} newVersion />
      {showTimeSelector && <TimePeriodSelector form={commonSelectorsForm} leave={leave} />}
      {hasBreaks && (
        <PlannedBreaksSelector
          plannedBreakForms={plannedBreakForms}
          onAddBreak={onAddBreak}
          onDeleteBreak={onDeleteBreak}
        />
      )}
      {locationsEnabled && showLocation && (
        <ShiftLocationPicker
          locationField={form.get('location_id')}
          workAreaField={form.get('work_area_id')}
          newVersion
        />
      )}
      {locationsEnabled && <ShiftNotesField notesField={form.get('notes')} newVersion />}
      {canManageDefaultShifts && (
        <SaveShiftToggle form={defaultShiftForm} defaultTitle={form.get('name').value} />
      )}
    </Box>
  )
}

export default observer(CustomShiftSection)
